import { Injectable } from '@angular/core';

export type LocationAccessKey = 'region' | 'campus' | 'facility' | 'locationGroup';
export type LocationAccessValue = string;
type LocationAccessUserKey = 'regions' | 'campuses' | 'facilities' | 'locationGroups';
type LocationDefaultUserKey = 'defaultRegions' | 'defaultCampuses' | 'defaultFacilities' | 'defaultLocationGroups';
export type LocationSelectionInput =
  | LocationAccessValue
  | LocationAccessOption
  | (LocationAccessValue | LocationAccessOption)[]
  | null
  | undefined;

export interface LocationAccessOption {
  label: string;
  value: LocationAccessValue;
}

export interface LocationFilter {
  region: LocationAccessValue[];
  campus: LocationAccessValue[];
  facility: LocationAccessValue[];
  locationGroup: LocationAccessValue[];
  [key: string]: unknown;
}

export interface LocationFilterEvent extends LocationFilter {
  changeValue: boolean;
}

export interface AuthorizedLocationFilter {
  regionId: LocationAccessValue[];
  campusId: LocationAccessValue[];
  facilityId: LocationAccessValue[];
  [key: string]: LocationAccessValue[] | unknown;
}

export interface LocationAccessUser {
  regions?: LocationSelectionInput;
  campuses?: LocationSelectionInput;
  facilities?: LocationSelectionInput;
  locationGroups?: LocationSelectionInput;
  defaultRegions?: LocationSelectionInput;
  defaultCampuses?: LocationSelectionInput;
  defaultFacilities?: LocationSelectionInput;
  defaultLocationGroups?: LocationSelectionInput;
}

export interface LocationSourceOption {
  label?: string;
  value?: LocationAccessValue | number;
  _id?: LocationAccessValue | number;
  id?: LocationAccessValue | number;
  name?: string;
  group?: string;
}

export interface LocationSelectionChangeEvent {
  value?: LocationAccessValue[];
}

export interface LocationFilterBreakdownLabels {
  allLarge: string;
  regionSelected: string;
  campusSelected: string;
  facilitySelected: string;
  locationGroupSelected: string;
  allSmall?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocationAccessService {
  private readonly accessKeys: Record<LocationAccessKey, LocationAccessUserKey> = {
    region: 'regions',
    campus: 'campuses',
    facility: 'facilities',
    locationGroup: 'locationGroups'
  };

  private readonly defaultKeys: Record<LocationAccessKey, LocationDefaultUserKey> = {
    region: 'defaultRegions',
    campus: 'defaultCampuses',
    facility: 'defaultFacilities',
    locationGroup: 'defaultLocationGroups'
  };

  normalizeValues(value: LocationSelectionInput, key?: LocationAccessKey): LocationAccessValue[] {
    if (value === undefined || value === null || value === '' || value === 'undefined') {
      return [];
    }

    const values = Array.isArray(value) ? value : [value];

    return values
      .map((item) => this.normalizeValue(item, key))
      .filter((item): item is LocationAccessValue => item !== undefined);
  }

  normalizeUserLocations(user?: LocationAccessUser): Record<LocationAccessKey, LocationAccessValue[]> {
    return {
      region: this.normalizeValues(user?.regions, 'region'),
      campus: this.normalizeValues(user?.campuses, 'campus'),
      facility: this.normalizeValues(user?.facilities, 'facility'),
      locationGroup: this.normalizeValues(user?.locationGroups, 'locationGroup')
    };
  }

  hasAll(values: LocationSelectionInput): boolean {
    return this.normalizeValues(values).includes('All');
  }

  isRestrictedFor(user: LocationAccessUser | undefined, key: LocationAccessKey): boolean {
    const values = this.normalizeValues(user?.[this.accessKeys[key]], key);
    return values.length > 0 && !values.includes('All');
  }

  getAuthorizedOptions(user: LocationAccessUser | undefined, key: LocationAccessKey, deId = false): LocationAccessOption[] {
    return this.normalizeValues(user?.[this.accessKeys[key]], key).map((value) => ({
      label: key === 'facility' && deId ? 'xxxxxx xxxxxx' : String(value),
      value
    }));
  }

  filterAuthorizedOptions(user: LocationAccessUser | undefined, key: LocationAccessKey, options: LocationAccessOption[], deId = false): LocationAccessOption[] {
    const allowedValues = this.normalizeValues(user?.[this.accessKeys[key]], key);

    if (!allowedValues.length || allowedValues.includes('All')) {
      return options;
    }

    const allowed = new Set(allowedValues.map((value) => String(value).toLowerCase()));

    return options
      .filter((option) => allowed.has(String(option.value).toLowerCase()) || allowed.has(String(option.label).toLowerCase()))
      .map((option) => ({
        ...option,
        label: key === 'facility' && deId ? 'xxxxxx xxxxxx' : option.label
      }));
  }

  sanitizeSelectedFilters(filter: Partial<LocationFilter> | undefined, user?: LocationAccessUser): LocationFilter {
    const safeFilter = filter ?? {};

    return {
      ...safeFilter,
      region: this.sanitizeSelectedValues(safeFilter.region, user, 'region'),
      campus: this.sanitizeSelectedValues(safeFilter.campus, user, 'campus'),
      facility: this.sanitizeSelectedValues(safeFilter.facility, user, 'facility'),
      locationGroup: this.sanitizeSelectedValues(safeFilter.locationGroup, user, 'locationGroup')
    };
  }

  sanitizeSelectedValues(values: LocationSelectionInput, user: LocationAccessUser | undefined, key: LocationAccessKey): LocationAccessValue[] {
    const selected = this.normalizeValues(values, key);

    if (!this.isRestrictedFor(user, key)) {
      return selected.length ? selected : ['All'];
    }

    const allowed = this.normalizeValues(user?.[this.accessKeys[key]], key);
    const allowedSet = new Set(allowed.map((value) => String(value).toLowerCase()));
    const sanitized = selected.filter((value) => allowedSet.has(String(value).toLowerCase()));

    return sanitized.length ? sanitized : allowed;
  }

  sanitizeSelectedOptionValues(values: LocationSelectionInput, options: LocationAccessOption[], user: LocationAccessUser | undefined, key: LocationAccessKey): LocationAccessValue[] {
    const selected = this.sanitizeSelectedValues(values, user, key);

    if (!this.isRestrictedFor(user, key)) {
      return selected;
    }

    const allowedOptionValues = new Set(options.map((option) => String(option.value).toLowerCase()));
    const matched = selected.filter((value) => allowedOptionValues.has(String(value).toLowerCase()));

    return matched.length ? matched : options.map((option) => option.value);
  }

  buildInitialFilters(user: LocationAccessUser | undefined, extra: Record<string, unknown> = {}): LocationFilter {
    const filter: LocationFilter = {
      region: this.initialValue(user, 'region'),
      campus: this.initialValue(user, 'campus'),
      facility: this.initialValue(user, 'facility'),
      locationGroup: this.initialValue(user, 'locationGroup'),
      ...extra
    };

    return this.sanitizeSelectedFilters(filter, user);
  }

  buildAuthorizedFilters(filter: Partial<LocationFilter>, user: LocationAccessUser | undefined, locationGroupKey = 'locationGroupingIds'): AuthorizedLocationFilter {
    const sanitized = this.sanitizeSelectedFilters(filter, user);
    return {
      regionId: sanitized.region,
      campusId: sanitized.campus,
      facilityId: sanitized.facility,
      [locationGroupKey]: sanitized.locationGroup
    };
  }

  applyAuthorizedFilters<T extends Record<string, unknown>>(
    target: T,
    source: Partial<LocationFilter>,
    user: LocationAccessUser | undefined,
    locationGroupKey = 'locationGroupingIds'
  ): T & AuthorizedLocationFilter {
    const sanitized = this.sanitizeSelectedFilters(source, user);
    const writableTarget = target as Record<string, unknown>;
    writableTarget['regionId'] = sanitized.region;
    writableTarget['campusId'] = sanitized.campus;
    writableTarget['facilityId'] = sanitized.facility;
    writableTarget[locationGroupKey] = sanitized.locationGroup;
    return target as T & AuthorizedLocationFilter;
  }

  getFilterBreakdown(
    filter: Partial<LocationFilter>,
    labels: LocationFilterBreakdownLabels,
    footprint?: string
  ): string {
    const sanitized = this.sanitizeSelectedFilters(filter, undefined);

    if (this.hasAll(sanitized.region)) {
      return footprint === 'small' && labels.allSmall ? labels.allSmall : labels.allLarge;
    }

    if (!this.hasAll(sanitized.locationGroup)) {
      return labels.locationGroupSelected;
    }

    if (!this.hasAll(sanitized.facility)) {
      return labels.facilitySelected;
    }

    if (!this.hasAll(sanitized.campus)) {
      return labels.campusSelected;
    }

    return labels.regionSelected;
  }

  private initialValue(user: LocationAccessUser | undefined, key: LocationAccessKey): LocationAccessValue[] {
    const defaultValues = this.normalizeValues(user?.[this.defaultKeys[key]], key);
    if (defaultValues.length && !defaultValues.includes('All')) {
      return defaultValues;
    }

    if (this.isRestrictedFor(user, key)) {
      return this.normalizeValues(user?.[this.accessKeys[key]], key);
    }

    return ['All'];
  }

  private normalizeValue(value: LocationAccessValue | LocationAccessOption, key?: LocationAccessKey): LocationAccessValue | undefined {
    if (this.isLocationSourceOption(value)) {
      if (key === 'locationGroup') {
        return this.toAccessValue(value.name ?? value.group ?? value.label ?? value.value ?? value._id);
      }

      return this.toAccessValue(value.value ?? value._id ?? value.id ?? value.label ?? value.name);
    }

    return this.toAccessValue(value);
  }

  private isLocationSourceOption(value: unknown): value is LocationSourceOption {
    return value !== null && typeof value === 'object';
  }

  private toAccessValue(value: unknown): LocationAccessValue | undefined {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    return undefined;
  }
}
